import * as R from 'ramda'
import {BLOCKS, INLINES, MARKS} from '@contentful/rich-text-types'
import {github} from 'react-syntax-highlighter/dist/esm/styles/hljs'
import {renderRichText} from 'gatsby-source-contentful/rich-text'
import React from 'react'
import SyntaxHighlighter from 'react-syntax-highlighter'

import {clearAndshrink, slugSwitch} from 'helpers/utils'
import {rawTextPropTypes} from 'helpers/propTypes'
import Description from 'components/UI/Description'
import LandingSection from 'components/Landing/LandingSection'
import Media from 'components/UI/Media'
import Title from 'components/UI/Title'
import useIsMobile from 'hooks/useIsMobile'

import useStyles from './styles'

const RawText = ({text, isArticle, shrink, isJustified, richTextDocuments}) => {
  const classes = useStyles()
  const isMobile = useIsMobile()

  const options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => {
        const isVideo = R.includes('iframe', children[0])
        const hasShrinkProp = !!shrink

        const hasInlineEntry = node.content.some(
          el => el.nodeType === 'embedded-entry-inline',
        )

        const createMarkup = () => ({__html: children[0]})

        const renderParagraph = () => {
          if (isVideo) {
            return (
              <div
                className={classes.embeddedVideo}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={createMarkup()}
              />
            )
          }
          if (hasShrinkProp) {
            const toStringRawText = R.join('', children)
            const shrinkContent = clearAndshrink(toStringRawText, shrink)

            return (
              <Description type="paragraph" isJustified={isJustified}>
                {shrinkContent}
              </Description>
            )
          }

          if (hasInlineEntry) {
            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                {children}
              </div>
            )
          }

          return (
            <Description type="paragraph" isJustified={isJustified}>
              {children}
            </Description>
          )
        }

        return renderParagraph()
      },
      [BLOCKS.HEADING_1]: (node, children) => (
        <Title
          type="mainTitle"
          fontFamily="medium"
          variant="h2"
          isRawText
          hasMarginTop
          hasSmallPaddingBottom
        >
          {children}
        </Title>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <Title
          type="mediumTitle"
          fontFamily="medium"
          variant="h2"
          isRawText
          hasMarginTop
          hasSmallPaddingBottom
        >
          {children}
        </Title>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <Title
          type="subTitle"
          fontFamily="book"
          variant="h3"
          isRawText
          hasMarginTop
          hasSmallPaddingBottom
        >
          {children}
        </Title>
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <Title type="innerTitle" fontFamily="book" variant="h4" isRawText>
          {children}
        </Title>
      ),
      [BLOCKS.HEADING_5]: (node, children) => (
        <Title type="smallTitle" fontFamily="book" variant="h5" isRawText>
          {children}
        </Title>
      ),
      [BLOCKS.HEADING_6]: (node, children) => (
        <Title type="verySmallTitle" fontFamily="book" variant="h6" isRawText>
          {children}
        </Title>
      ),
      [BLOCKS.EMBEDDED_ASSET]: ({data}) => (
        <div className={classes.embeddedImage}>
          <Media data={data.target} isFullWidth />
          {/* <Description
            type="paragraph"
            isJustified
            isCentered
            isItalic
            hasMarginTop
            hasMarginBottom
          >
            {data?.target?.description}
          </Description> */}
        </div>
      ),
      [BLOCKS.QUOTE]: (node, children) => (
        <span className={classes.blockQuote}>
          <span className={classes.quoteDecoration} />
          <span className={classes.textBlockQuote}>{children}</span>
        </span>
      ),
      [INLINES.HYPERLINK]: (node, children) => {
        const uri = R.pathOr(null, ['data', 'uri'], node)

        return (
          <a
            href={uri}
            className={classes.link}
            target="_blank"
            rel="noreferrer"
          >
            {children}
          </a>
        )
      },
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        const targetSlug = slugSwitch(
          R.pathOr(null, ['data', 'target', 'slug'], node),
        )
        const isDataProtection = R.equals('data-protection', targetSlug)
        const isPrivacyPolicy = R.equals('privacy-policy', targetSlug)
        const isLegalNotice = R.equals(
          'legal-notice-and-terms-of-use',
          targetSlug,
        )
        const isDocument =
          R.pathOr(null, ['data', 'target', '__typename'], node) ===
          'ContentfulDocument'

        const removeSpacesAndCharacters = str =>
          str.replace(/[^a-zA-Z0-9]/g, '')

        const generateURL = (str, baseUrl) => baseUrl + str

        const handleHref = () => {
          if (isDataProtection) {
            return `about-us/${targetSlug}`
          }
          if (isArticle && (isPrivacyPolicy || isLegalNotice)) {
            const hasTrailingSlash = window.location.pathname.endsWith('/')

            return hasTrailingSlash ? `../../${targetSlug}` : `../${targetSlug}`
          }

          if (isDocument) {
            const contentfulIdToFilter = R.pathOr(
              null,
              ['data', 'target', 'contentful_id'],
              node,
            )
            const filteredData = R.filter(
              obj => obj.contentful_id === contentfulIdToFilter,
              richTextDocuments,
            )
            const cleanFileName = removeSpacesAndCharacters(
              filteredData[0].name,
            )

            const urlToRedirectTo = generateURL(
              cleanFileName,
              'https://keyrus.com/',
            )

            return urlToRedirectTo
          }

          return targetSlug
        }

        return (
          <a
            href={handleHref()}
            className={classes.link}
            target="_blank"
            rel="noreferrer"
          >
            {children}
          </a>
        )
      },
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        const {url} = R.pathOr(null, ['node', 'data', 'target', 'file'])

        return (
          <a
            href={url}
            className={classes.link}
            target="_blank"
            rel="noreferrer"
          >
            {children}
          </a>
        )
      },

      [BLOCKS.TABLE]: (node, children) => (
        <table className={classes.table}>
          <thead>{children[0]}</thead>
          <tbody>{children.slice(1)}</tbody>
        </table>
      ),
      [BLOCKS.TABLE_ROW]: (node, children) => (
        <tr className={classes.tableRow}>{children}</tr>
      ),
      [BLOCKS.TABLE_CELL]: (node, children) => (
        <td className={classes.tableCell}>{children}</td>
      ),
      [BLOCKS.TABLE_HEADER_CELL]: (node, children) => (
        <th className={classes.tableHeaderCell}>{children}</th>
      ),

      [BLOCKS.EMBEDDED_ENTRY]: node => {
        const content = R.pathOr(null, ['data', 'target'], node)

        return <LandingSection content={content} />
      },
      [INLINES.EMBEDDED_ENTRY]: node => {
        const entry = R.pathOr(null, ['data', 'target'], node)

        return <LandingSection isInline content={entry} />
      },
    },
    renderMark: {
      [MARKS.BOLD]: content => (
        <span className={classes.boldText}>{content}</span>
      ),
      [MARKS.CODE]: content => (
        <SyntaxHighlighter language="javascript" style={github}>
          {content}
        </SyntaxHighlighter>
      ),
    },
  }

  return <>{text && renderRichText(text, options)}</>
}

RawText.propTypes = rawTextPropTypes.isRequired

RawText.defaultProps = {
  isArticle: false,
  isJustified: false,
  shrink: false,
  text: {
    raw: '',
    references: null,
  },
}

export default RawText
